import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Search, Filter, User } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/services/api';
import { formatDate, getProtectionLevelLabel } from '@/lib/utils';
import type { Prisoner, PrisonerStatus } from '@/types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Haftzeit-Fortschrittsbalken Komponente
function SentenceProgress({ 
  admissionDate, 
  releaseDate 
}: { 
  admissionDate: string; 
  releaseDate: string | null;
}) {
  const admission = new Date(admissionDate);
  const today = new Date();
  const release = releaseDate ? new Date(releaseDate) : null;

  // Formatiere Datum kurz (TT.MM.JJ)
  const formatShort = (date: Date) => {
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: '2-digit' 
    });
  };

  // Wenn kein Entlassungsdatum, zeige nur Aufnahme
  if (!release) {
    return (
      <div className="w-44">
        <div className="flex justify-between text-xs text-muted-foreground mb-1">
          <span>{formatShort(admission)}</span>
          <span className="text-amber-600">unbefristet</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-amber-500 rounded-full animate-pulse" style={{ width: '100%' }} />
        </div>
      </div>
    );
  }

  // Berechne Fortschritt
  const totalDays = Math.max(1, (release.getTime() - admission.getTime()) / (1000 * 60 * 60 * 24));
  const elapsedDays = Math.max(0, (today.getTime() - admission.getTime()) / (1000 * 60 * 60 * 24));
  const progress = Math.min(100, Math.max(0, (elapsedDays / totalDays) * 100));
  
  // Verbleibende Tage
  const remainingDays = Math.max(0, Math.ceil((release.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));

  // Farbe basierend auf Fortschritt
  const getProgressColor = () => {
    if (progress >= 90) return 'bg-green-500';
    if (progress >= 75) return 'bg-emerald-500';
    if (progress >= 50) return 'bg-blue-500';
    if (progress >= 25) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="w-44">
      <div className="flex justify-between text-xs text-muted-foreground mb-1">
        <span>{formatShort(admission)}</span>
        <span>{formatShort(release)}</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full ${getProgressColor()} rounded-full transition-all duration-300`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="text-xs text-center mt-1 text-muted-foreground">
        {remainingDays > 0 ? (
          <span>noch {remainingDays} {remainingDays === 1 ? 'Tag' : 'Tage'}</span>
        ) : (
          <span className="text-green-600 font-medium">Haft beendet</span>
        )}
      </div>
    </div>
  );
}

export function PrisonerListPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [photoBlobUrls, setPhotoBlobUrls] = useState<Record<string, string>>({});
  const pageSize = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['prisoners', page, search],
    queryFn: () => api.getPrisoners({ page, pageSize, search }),
  });

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const loadPhotos = async () => {
      const prisoners = data?.data ?? [];
      const token = localStorage.getItem('token');

      if (!token || prisoners.length === 0) {
        setPhotoBlobUrls((prev) => {
          Object.values(prev).forEach((url) => URL.revokeObjectURL(url));
          return {};
        });
        return;
      }

      const nextUrls: Record<string, string> = {};

      for (const prisoner of prisoners) {
        if (!prisoner.photoUrl) continue;
        try {
          const response = await fetch(`${API_URL}${prisoner.photoUrl}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
          });
          if (!response.ok) continue;
          const blob = await response.blob();
          nextUrls[prisoner.id] = URL.createObjectURL(blob);
        } catch {
          // Ignore single image failures to keep list usable.
        }
      }

      if (!active) {
        Object.values(nextUrls).forEach((url) => URL.revokeObjectURL(url));
        return;
      }

      setPhotoBlobUrls((prev) => {
        Object.values(prev).forEach((url) => URL.revokeObjectURL(url));
        return nextUrls;
      });
    };

    loadPhotos();

    return () => {
      active = false;
      controller.abort();
    };
  }, [data?.data]);

  const getStatusBadge = (status: PrisonerStatus) => {
    const styles: Record<PrisonerStatus, string> = {
      ACTIVE: 'bg-green-100 text-green-800',
      RELEASED: 'bg-gray-100 text-gray-800',
      TRANSFERRED: 'bg-blue-100 text-blue-800',
      DECEASED: 'bg-red-100 text-red-800',
    };
    const labels: Record<PrisonerStatus, string> = {
      ACTIVE: 'Aktiv',
      RELEASED: 'Entlassen',
      TRANSFERRED: 'Verlegt',
      DECEASED: 'Verstorben',
    };
    return (
      <span className={`rounded-full px-2 py-1 text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Gefangene</h1>
          <p className="text-muted-foreground">
            Verwalten Sie die Gefangenenpersonalakten
          </p>
        </div>
        <Button asChild>
          <Link to="/prisoners/new">
            <Plus className="mr-2 h-4 w-4" />
            Neue Aufnahme
          </Link>
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Nach Name oder Nummer suchen..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button variant="outline">
              <Filter className="mr-2 h-4 w-4" />
              Filter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Gefangenenliste</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">
              Lädt...
            </div>
          ) : !data?.data.length ? (
            <div className="py-8 text-center text-muted-foreground">
              Keine Gefangenen gefunden.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm font-medium text-muted-foreground">
                    <th className="pb-3 pr-4 w-16">Foto</th>
                    <th className="pb-3 pr-4">Nummer</th>
                    <th className="pb-3 pr-4">Name</th>
                    <th className="pb-3 pr-4">Geburtsdatum</th>
                    <th className="pb-3 pr-4">Haftzeit</th>
                    <th className="pb-3 pr-4">Status</th>
                    <th className="pb-3 pr-4">Schutzstufe</th>
                    <th className="pb-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((prisoner: Prisoner) => (
                    <tr
                      key={prisoner.id}
                      className="border-b last:border-0 hover:bg-muted/50"
                    >
                      <td className="py-3 pr-4">
                        <div className="h-10 w-10 rounded-full overflow-hidden bg-muted flex items-center justify-center">
                          {photoBlobUrls[prisoner.id] ? (
                            <img
                              src={photoBlobUrls[prisoner.id]}
                              alt={`${prisoner.firstName} ${prisoner.lastName}`}
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                          ) : null}
                          <User className={`h-5 w-5 text-muted-foreground ${photoBlobUrls[prisoner.id] ? 'hidden' : ''}`} />
                        </div>
                      </td>
                      <td className="py-3 pr-4 font-mono text-sm">
                        {prisoner.prisonerNumber}
                      </td>
                      <td className="py-3 pr-4">
                        <Link
                          to={`/prisoners/${prisoner.id}`}
                          className="font-medium text-primary hover:underline"
                        >
                          {prisoner.lastName}, {prisoner.firstName}
                        </Link>
                      </td>
                      <td className="py-3 pr-4 text-sm">
                        {formatDate(prisoner.dateOfBirth)}
                      </td>
                      <td className="py-3 pr-4">
                        <SentenceProgress 
                          admissionDate={prisoner.admissionDate} 
                          releaseDate={prisoner.releaseDate ?? null} 
                        />
                      </td>
                      <td className="py-3 pr-4">
                        {getStatusBadge(prisoner.status)}
                      </td>
                      <td className="py-3 pr-4 text-sm">
                        {getProtectionLevelLabel(prisoner.protectionLevel)}
                      </td>
                      <td className="py-3">
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/prisoners/${prisoner.id}`}>
                            Akte öffnen
                          </Link>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Seite {page} von {data.totalPages} ({data.total} Einträge)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  Zurück
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === data.totalPages}
                >
                  Weiter
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
